import React, { ReactNode, useEffect, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { VehicleMasterTable } from "../data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";

import {
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  FormField,
  Input,
  SpaceBetween,
  StatusIndicator,
  Box,
  Button,
  TextFilter,
  Pagination,
  Modal,
  Flashbar,
} from "@cloudscape-design/components";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import {
  createVehicle,
  deleteVehicle,
  updateVehicle,
} from "../../../../graphql/mutations";
import { getCurrentUser } from "aws-amplify/auth";
import { fetchAuthSession } from "aws-amplify/auth";

// get user belong to what group and make it into string
async function checkUserGroup() {
  try {
    const { tokens } = await fetchAuthSession();
    // Normalize user groups to be an array of strings
    const groups = tokens?.accessToken.payload["cognito:groups"];
    if (Array.isArray(groups)) {
      return groups as string[]; // Safe to cast as string[]
    } else if (typeof groups === "string") {
      return [groups]; // Convert a single string to an array
    } else {
      return []; // Default to an empty array if not an array or string
    }
  } catch (error) {
    console.error("Error fetching user groups:", error);
    return []; // Default to an empty array in case of an error
  }
}

async function checkCurrentUser(): Promise<{
  username: string;
  email: string | null;
}> {
  try {
    const { tokens } = await fetchAuthSession();

    // Extract the user's information from the accessToken or idToken
    const currentUser =
      tokens?.idToken?.payload || tokens?.accessToken?.payload;

    if (currentUser) {
      const username =
        typeof currentUser["cognito:username"] === "string"
          ? currentUser["cognito:username"]
          : ""; // Fallback to an empty string if username is not a string

      // Check if email is a string, otherwise default to null
      const email =
        typeof currentUser.email === "string" ? currentUser.email : null; // Fallback to null if email is not a string

      return { username, email }; // Return user info with valid types
    } else {
      console.error("No user information found.");
      return { username: "", email: null }; // Return empty values if no user data
    }
  } catch (error) {
    console.error("Error fetching current user information:", error);
    return { username: "", email: null }; // Default to empty values in case of error
  }
}

interface FlashbarItem {
  type: "info" | "warning" | "error" | "success"; // Adjust according to your requirements
  dismissible: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  content: React.ReactNode;
  id: string;
}

async function createVehicleApi(
  vehiclecode: string,
  typevehicle: string,
  location: string,
  status: boolean,
  category: string
) {
  try {
    const client = generateClient();
    await client.graphql({
      query: createVehicle,
      authMode: "userPool",
      variables: {
        input: {
          vehiclecode: vehiclecode,
          typevehicle: typevehicle,
          location: location,
          status: status,
          category: category,
        },
      },
    });
  } catch (err) {
    console.log(err);
  }
}

async function deleteVehicleApi(id: string) {
  try {
    const client = generateClient();
    await client.graphql({
      query: deleteVehicle,
      authMode: "userPool",
      variables: {
        input: {
          id,
        },
      },
    });
  } catch (err) {
    console.log("Error deleting vehicle: ", err);
  }
}

async function updateVehicleApi(
  id: string,
  vehiclecode: string,
  typevehicle: string,
  location: string,
  status: boolean,
  category: string
) {
  try {
    const client = generateClient();
    await client.graphql({
      query: updateVehicle,
      authMode: "userPool",
      variables: {
        input: {
          id: id,
          vehiclecode: vehiclecode,
          typevehicle: typevehicle,
          location: location,
          status: status,
          category: category,
        },
      },
    });
  } catch (err) {
    console.log("Error updating vehicle: ", err);
  }
}

interface CellProps {
  Status: boolean;
}

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  VehicleMaster: VehicleMasterTable[];
  refreshData: () => void;
}

const DeviceStatus: React.FC<CellProps> = ({ Status }) => {
  let statusType: "success" | "error" = "error";

  if (Status === true) {
    statusType = "success";
  } else if (Status === false) {
    statusType = "error";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success" ? "Active" : "Deactivated"}
    </StatusIndicator>
  );
};

const columnDefinitions: TableProps<VehicleMasterTable>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "ID",
    width: 70,
    minWidth: 70,
    maxWidth: 100,
  },
  {
    header: "Vehicle Code",
    cell: ({ VehicleCode }) => (
      <Link to={`/vehicles/${VehicleCode}`}>{VehicleCode}</Link>
    ),
    sortingField: "VehicleCode",
    minWidth: 160,
  },
  {
    header: "Type",
    cell: ({ TypeVehicle }) => TypeVehicle,
    sortingField: "TypeVehicle",
    minWidth: 160,
  },
  {
    header: "Location ",
    cell: ({ Location }) => Location,
    sortingField: "Location",
    minWidth: 150,
  },
  {
    header: "Category",
    cell: ({ Category }) => Category,
    sortingField: "category",
    minWidth: 150,
  },
  {
    header: "Status ",
    cell: DeviceStatus,
    sortingField: "Status",
    minWidth: 150,
  },
  {
    header: "Last Update",
    cell: ({ Timestamps }) => new Date(Timestamps).toLocaleString(), // Display timestamp in a readable format
    sortingField: "Timestamps",
    sortingComparator: (a, b) =>
      new Date(a.Timestamps).getTime() - new Date(b.Timestamps).getTime(), // Compare timestamps as numbers
    minWidth: 150,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly VehicleMasterTable[] = [],
  selectedItems: readonly VehicleMasterTable[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VariationTable({
  VehicleMaster,
  refreshData,
}: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isModalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleLocation, setVehicleLocation] = useState("");
  const [vehicleStatus, setVehicleStatus] = useState<boolean | null>(null);
  const [vehicleCategory, setVehicleCategory] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null
  );
  const [itemsFlashbar, setItemsFlashbar] = useState<FlashbarItem[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string>("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // TimeStamp for log Format
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  // call all the necessary funtion on page load
  useEffect(() => {
    async function initialize() {
      const groups = await checkUserGroup();
      const user = await checkCurrentUser();
      setCurrentUser(user.username);
      setCurrentUserEmail(user.email ?? "");
      setUserGroups(groups);
      setChecked(vehicleStatus === true);
    }
    initialize();
  }, [vehicleStatus]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const status = vehicleStatus !== null ? vehicleStatus : false;

      if (selectedVehicleId) {
        await updateVehicleApi(
          selectedVehicleId,
          vehicleCode,
          vehicleType,
          vehicleLocation,
          status,
          vehicleCategory
        );

        // for transaction logs
        console.log(
          `Vehicle Transaction Log (Update Item):
        Vehicle ID: ${selectedVehicleId}
        Vehicle Code: ${vehicleCode}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );
        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "Vehicle successfully updated.",
            id: "message_3",
          },
        ]);
      } else {
        await createVehicleApi(
          vehicleCode,
          vehicleType,
          vehicleLocation,
          status,
          vehicleCategory
        );
        // for transaction logs
        console.log(
          `Vehicle Transaction Log (Create Item):
        Vehicle Code: ${vehicleCode}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );
        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "Vehicle successfully created.",
            id: "message_2",
          },
        ]);
      }

      refreshData();
      setVisible(false);
      setVisible2(false);
      clearFormState();
    } catch (error) {
      console.error("Error updating/creating vehicle:", error);
      setItemsFlashbar([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "An error occurred. Please try again.",
          id: "message_error",
        },
      ]);
    } finally {
      setIsSubmitting(false); // Re-enable the submit button after completion
    }
  };

  const handleDelete = async () => {
    const selectedItem = collectionProps.selectedItems?.[0];
    if (selectedItem) {
      await deleteVehicleApi(selectedItem.id);
      setModalDeleteVisible(false);
      // for transaction logs
      console.log(
        `Vehicle Transaction Log (Delete Item):
        Vehicle ID: ${selectedItem.id}
        Vehicle Code: ${selectedItem.VehicleCode}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
      );
      refreshData();
      setItemsFlashbar([
        {
          type: "warning",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "Vehicle successfully deleted !",
          id: "message_1",
        },
      ]);
    }
  };

  const handleDeleteCOnfirmation = () => {
    setModalDeleteVisible(true);
  };

  const clearFormState = () => {
    setVehicleCode("");
    setVehicleType("");
    setVehicleLocation("");
    setVehicleStatus(null);
    setVehicleCategory("");
    setChecked(false);
    setSelectedVehicleId(null);
  };

  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<VehicleMasterTable>(VehicleMaster, {
    filtering: {
      filteringFunction: (item, filteringText) => {
        const filterTextLower = filteringText.toLowerCase();

        // Convert boolean to string for Status comparison
        const statusText = item.Status ? "active" : "deactivated";

        // Match directy with the status text ("Active" or "Deactivated")
        if (statusText.includes(filterTextLower)) {
          return true;
        }

        return (
          item.id.toLowerCase().includes(filterTextLower) ||
          item.VehicleCode.toLowerCase().includes(filterTextLower) ||
          item.TypeVehicle.toLowerCase().includes(filterTextLower) ||
          item.Location.toLowerCase().includes(filterTextLower) ||
          item.Category.toLowerCase().includes(filterTextLower) ||
          item.Timestamps.toLowerCase().includes(filterTextLower)
        );
      },
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });

  // To disable or enable button CRUD
  const isButtonDisabled =
    !userGroups.includes("super_admin") && !userGroups.includes("tenant_admin");
  const isUpdateButtonDisabled =
    !userGroups.includes("super_admin") &&
    !userGroups.includes("tenant_admin") &&
    !userGroups.includes("operator");

  return (
    <>
      <Modal
        onDismiss={() => setModalDeleteVisible(false)}
        visible={isModalDeleteVisible}
        closeAriaLabel='Close modal'
        header='Confirm Delete'
        footer={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => setModalDeleteVisible(false)}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleDelete}>
              Confirm
            </Button>
          </SpaceBetween>
        }
      >
        <p>Are you sure you want to delete this Unit ?</p>
      </Modal>
      <Modal
        onDismiss={() => {
          setVisible(false);
          clearFormState();
        }}
        visible={visible}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !vehicleCode ||
                  !vehicleType ||
                  !vehicleCategory ||
                  !vehicleLocation
                }
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Add Vehicle'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Vehicle Code'>
              <Input
                value={vehicleCode}
                onChange={({ detail }) => setVehicleCode(detail.value)}
              />
            </FormField>
            <FormField label='Vehicle Type'>
              <Input
                value={vehicleType}
                onChange={({ detail }) => setVehicleType(detail.value)}
              />
            </FormField>
            <FormField label='Category'>
              <Input
                value={vehicleCategory}
                onChange={({ detail }) => setVehicleCategory(detail.value)}
              />
            </FormField>
            <FormField label='Location'>
              <Input
                value={vehicleLocation}
                onChange={({ detail }) => setVehicleLocation(detail.value)}
              />
            </FormField>
            <Checkbox
              onChange={({ detail }) => {
                setChecked(detail.checked);
                setVehicleStatus(detail.checked);
              }}
              checked={checked}
            >
              Active
            </Checkbox>
          </SpaceBetween>
        </Container>
      </Modal>

      <Modal
        onDismiss={() => {
          setVisible2(false);
          clearFormState();
        }}
        visible={visible2}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible2(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button variant='primary' onClick={handleSubmit}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Edit Vehicle'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Vehicle Code'>
              <Input
                value={vehicleCode}
                onChange={({ detail }) => setVehicleCode(detail.value)}
              />
            </FormField>
            <FormField label='Vehicle Type'>
              <Input
                value={vehicleType}
                onChange={({ detail }) => setVehicleType(detail.value)}
              />
            </FormField>
            <FormField label='Category'>
              <Input
                value={vehicleCategory}
                onChange={({ detail }) => setVehicleCategory(detail.value)}
              />
            </FormField>
            <FormField label='Location'>
              <Input
                value={vehicleLocation}
                onChange={({ detail }) => setVehicleLocation(detail.value)}
              />
            </FormField>
            <Checkbox
              onChange={({ detail }) => {
                setChecked(detail.checked);
                setVehicleStatus(detail.checked);
              }}
              checked={checked}
            >
              Active
            </Checkbox>
          </SpaceBetween>
        </Container>
      </Modal>
      <Flashbar items={itemsFlashbar} />
      <Table<VehicleMasterTable>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Vehicle table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              VehicleMaster,
              collectionProps.selectedItems ?? [],
              items.length
            )}
            actions={
              <SpaceBetween size='s' direction='horizontal'>
                <Button
                  disabled={
                    (collectionProps.selectedItems ?? []).length === 0 ||
                    isButtonDisabled
                  }
                  onClick={handleDeleteCOnfirmation}
                >
                  Delete
                </Button>
                <Button
                  disabled={
                    (collectionProps.selectedItems ?? []).length === 0 ||
                    isUpdateButtonDisabled
                  }
                  onClick={() => {
                    const selectedItem = collectionProps.selectedItems?.[0];
                    if (selectedItem) {
                      setVehicleCode(selectedItem.VehicleCode);
                      setVehicleType(selectedItem.TypeVehicle);
                      setVehicleCategory(selectedItem.Category);
                      setVehicleLocation(selectedItem.Location);
                      setVehicleStatus(selectedItem.Status);
                      setSelectedVehicleId(selectedItem.id);
                      setVisible2(true);
                    }
                  }}
                  variant='primary'
                >
                  Update
                </Button>
                <Button
                  disabled={isButtonDisabled}
                  onClick={() => setVisible(true)}
                  variant='primary'
                >
                  Add Vehicle
                </Button>
              </SpaceBetween>
            }
          >
            Vehicles
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Vehicle'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "Select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='Preferences'
            confirmLabel='Confirm'
            cancelLabel='Cancel'
          />
        }
        selectionType='single'
      />
    </>
  );
}
